"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import NextLink from "next/link";
import {
  ArrowRight,
  BarChart3,
  BookOpen,
  ChevronDown,
  Landmark,
  Megaphone,
  PackageOpen,
  Trophy,
  type LucideIcon,
} from "lucide-react";
import { Link } from "@/i18n/navigation";
import SignToggle from "@/components/sign/toggle";
import { mirofishNavLinks } from "@/content/mirofish";
import type { PredictionDomainIcon } from "@/content/prediction-page";
import { BRAND_NAME, VISIBLE_LOGO_SRC } from "@/lib/brand";

type MiroFishNavigationMode = "localized" | "pt-market-research";

type PredictionMenuItem = Extract<
  (typeof mirofishNavLinks)[number],
  { kind: "prediction-menu" }
>;

const predictionIcons: Record<PredictionDomainIcon, LucideIcon> = {
  sports: Trophy,
  audience: Megaphone,
  policy: Landmark,
  product: PackageOpen,
  market: BarChart3,
  story: BookOpen,
};

function PredictionMenu({
  item,
  mobile = false,
  navigationMode = "localized",
}: {
  item: PredictionMenuItem;
  mobile?: boolean;
  navigationMode?: MiroFishNavigationMode;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuId = mobile
    ? "mirofish-mobile-use-cases-menu"
    : "mirofish-use-cases-menu";

  useEffect(() => {
    if (!open) {
      return;
    }

    const closeOutside = (event: PointerEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeOutside);
    return () => document.removeEventListener("pointerdown", closeOutside);
  }, [open]);

  return (
    <div
      ref={menuRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          setOpen(false);
          (event.target as HTMLElement).blur();
        }
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-haspopup="true"
        onClick={() => setOpen((current) => !current)}
        className={
          mobile
            ? "inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.055] px-3 py-2 text-xs font-medium text-white/76 transition hover:border-emerald-200/25 hover:bg-white/[0.09] hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/55"
            : "inline-flex items-center gap-1.5 py-2 text-sm text-white/70 transition hover:text-white focus-visible:outline-none focus-visible:text-white"
        }
        data-testid={
          mobile ? "use-cases-mobile-menu-trigger" : "use-cases-menu-trigger"
        }
      >
        {item.label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${
            open ? "rotate-180 text-emerald-200" : "text-white/36"
          }`}
        />
      </button>

      <div
        id={menuId}
        className={`absolute top-full z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-[24px] border border-white/12 bg-[#070a0f]/98 p-2 shadow-[0_24px_80px_rgba(0,0,0,0.68)] backdrop-blur-2xl transition duration-200 ${
          mobile ? "right-0" : "left-1/2 -translate-x-1/2"
        } ${
          open
            ? "visible translate-y-0 opacity-100"
            : "pointer-events-none invisible -translate-y-1 opacity-0"
        }`}
        data-testid={mobile ? "use-cases-mobile-menu" : "use-cases-menu"}
      >
        <div className="px-3 pb-2 pt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-emerald-200/55">
          Use cases
        </div>
        <div className="space-y-1">
          {item.items.map((prediction) => {
            const Icon = predictionIcons[prediction.icon];
            const content = (
              <>
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-emerald-200/15 bg-emerald-300/[0.07] text-emerald-200 transition group-hover/link:bg-emerald-300/[0.11]">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0">
                  <span className="block font-mono text-[9px] uppercase tracking-[0.18em] text-white/36">
                    {prediction.category}
                  </span>
                  <span className="mt-0.5 block text-sm font-semibold text-white">
                    {prediction.label}
                  </span>
                  <span className="mt-1 block text-xs leading-5 text-white/45">
                    {prediction.description}
                  </span>
                </span>
              </>
            );
            const className =
              "group/link flex items-center gap-3 rounded-[18px] border border-transparent px-3 py-3 transition hover:border-white/10 hover:bg-white/[0.055] focus-visible:border-emerald-200/30 focus-visible:bg-white/[0.055] focus-visible:outline-none";
            const onClick = () => setOpen(false);

            return navigationMode === "pt-market-research" ? (
              <NextLink
                key={prediction.href}
                href={prediction.href}
                onClick={onClick}
                className={className}
              >
                {content}
              </NextLink>
            ) : (
              <Link
                key={prediction.href}
                href={prediction.href}
                onClick={onClick}
                className={className}
              >
                {content}
              </Link>
            );
          })}
        </div>
        <div className="mt-2 border-t border-white/10 px-3 pb-2 pt-3">
          {navigationMode === "pt-market-research" ? (
            <NextLink
              href={item.collectionHref}
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/44 transition hover:text-emerald-100 focus-visible:outline-none focus-visible:text-emerald-100"
            >
              View all use cases
              <ArrowRight className="h-3.5 w-3.5" />
            </NextLink>
          ) : (
            <Link
              href={item.collectionHref}
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/44 transition hover:text-emerald-100 focus-visible:outline-none focus-visible:text-emerald-100"
            >
              View all use cases
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function MiroFishSiteHeader({
  hashHrefPrefix = "",
  navigationMode = "localized",
}: {
  hashHrefPrefix?: "" | "/";
  navigationMode?: MiroFishNavigationMode;
}) {
  const predictionMenu = mirofishNavLinks.find(
    (item): item is PredictionMenuItem => item.kind === "prediction-menu"
  );

  return (
    <header
      className="sticky top-0 z-40 border-b border-white/10 bg-black/30 backdrop-blur-xl"
      data-testid="mirofish-site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-4 sm:px-6 lg:px-8">
        {navigationMode === "pt-market-research" ? (
          <NextLink href="/" className="flex items-center gap-3 text-white">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/5">
              <Image
                src={VISIBLE_LOGO_SRC}
                alt={BRAND_NAME}
                width={44}
                height={44}
                className="h-full w-full object-cover"
                data-brand-logo="header"
              />
            </div>
            <div className="hidden sm:block">
              <div className="text-xs uppercase tracking-[0.28em] text-white/45">
                Predict Anything
              </div>
              <div className="text-lg font-semibold tracking-[0.08em]">{BRAND_NAME.toUpperCase()}</div>
            </div>
          </NextLink>
        ) : (
          <Link href="/" className="flex items-center gap-3 text-white">
            <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl border border-white/10 bg-white/5">
              <Image
                src={VISIBLE_LOGO_SRC}
                alt={BRAND_NAME}
                width={44}
                height={44}
                className="h-full w-full object-cover"
                data-brand-logo="header"
              />
            </div>
            <div className="hidden sm:block">
              <div className="text-xs uppercase tracking-[0.28em] text-white/45">
                Predict Anything
              </div>
              <div className="text-lg font-semibold tracking-[0.08em]">
                {BRAND_NAME.toUpperCase()}
              </div>
            </div>
          </Link>
        )}

        <nav
          aria-label="Primary navigation"
          className="hidden items-center gap-6 lg:flex"
        >
          {mirofishNavLinks.map((item) => {
            if (item.kind === "prediction-menu") {
              return (
                <PredictionMenu
                  key={item.label}
                  item={item}
                  navigationMode={navigationMode}
                />
              );
            }

            return item.kind === "hash" ? (
              <a
                key={item.href}
                href={`${hashHrefPrefix}${item.href}`}
                className="text-sm text-white/70 transition hover:text-white"
              >
                {item.label}
              </a>
            ) : navigationMode === "pt-market-research" ? (
              <NextLink
                key={item.href}
                href={item.href}
                className="text-sm text-white/70 transition hover:text-white"
              >
                {item.label}
              </NextLink>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className="text-sm text-white/70 transition hover:text-white"
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          {predictionMenu ? (
            <div className="lg:hidden">
              <PredictionMenu
                item={predictionMenu}
                mobile
                navigationMode={navigationMode}
              />
            </div>
          ) : null}
          <SignToggle />
        </div>
      </div>
    </header>
  );
}
